import { Link } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { UserAvatar } from "@/components/shared/user-avatar";
import { Button } from "@/components/ui/button";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { PageHeaderProvider, usePageHeaderContent } from "./PageHeaderContext";
import { Sidebar } from "./Sidebar";
import { SidebarProvider, useSidebar } from "./SidebarContext";
import { ThemeToggle } from "./ThemeToggle";

function DashboardLayoutInner({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const { isMobile, setMobileOpen } = useSidebar();
  const pageHeader = usePageHeaderContent();

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header
          className={cn(
            "flex min-h-16 items-center justify-between gap-3 border-b border-border bg-background px-4 py-2 md:px-6",
            // A fixed h-16 forced one line, so a page header with two selects
            // in it pushed the theme toggle and avatar off-screen at 1024
            // (measured at x=1208). Wrapping is the honest answer: the bar
            // grows a row rather than hiding its own controls.
          )}
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(true)}
                className="shrink-0"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            {pageHeader ? (
              pageHeader
            ) : isMobile ? (
              <span className="font-display text-lg font-bold truncate">Nymbl Profitability</span>
            ) : (
              <div className="text-sm text-muted-foreground">Dashboard</div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <ThemeToggle />
            {session?.user && (
              <Link
                to="/dashboard/profile"
                title="Profile"
                className="rounded-full ring-offset-background transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <UserAvatar user={session.user} className="h-8 w-8" />
              </Link>
            )}
          </div>
        </header>

        {/* Scrollable content */}
        {/*
          Full width, deliberately. The template caps content at max-w-5xl, which
          suits forms and settings but not this app: every primary screen here is
          a financial table of seven or eight columns, and a 1024px cap turns
          revenue, cost, margin, budget and rate columns into a horizontal
          scroll. Settings pages have their own SettingsLayout and are
          unaffected.
        */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="w-full">{children}</div>
        </main>
      </div>
    </div>
  );
}

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <PageHeaderProvider>
        <DashboardLayoutInner>{children}</DashboardLayoutInner>
      </PageHeaderProvider>
    </SidebarProvider>
  );
}
